import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Button,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Refresh } from "@material-ui/icons";
import { useAuth } from "../context/ApiKeyContext";
import { useTranslation } from "../hooks/useTranslation";
import JobHeader from "./JobHeader";
import JobTable from "./JobTable";
import JobFilters from "./JobFilters";
import JobPagination from "./JobPagination";
import CellConfirmationModal from "./CellConfirmationModal";
import { getApiBaseUrl } from "../config";
import { getEmbedListingQuery, isEmbedMode } from "../constants/embedMode";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--ikaro-gray-light)",
    overflow: "hidden",
    minHeight: 0, // Importante para que o flex funcione corretamente
  },
  // Header fixo no topo
  headerSection: {
    flexShrink: 0,
  },
  // Filtros (quando visíveis)
  filtersSection: {
    flexShrink: 0,
    padding: "0 16px",
  },
  // Container principal da tabela - ocupa o espaço restante
  tableSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: 0, // Importante para que o flex funcione corretamente
  },
  // Versão compacta do container para páginas de jobs
  containerCompact: {
    padding: 0,
    margin: 0,
    maxWidth: "none",
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  container: {
    padding: theme.spacing(3),
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
  paper: {
    backgroundColor: "var(--ikaro-text-white)",
    borderRadius: 0,
    boxShadow: "none",
    border: "1px solid var(--ikaro-gray-medium)",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  // Container para a tabela com scroll
  tableWrapper: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  // Paginação fixa na base
  paginationSection: {
    flexShrink: 0,
  },
  refreshButton: {
    borderColor: "var(--ikaro-orange-primary)",
    color: "var(--ikaro-orange-primary)",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
    },
    "&:disabled": {
      borderColor: "var(--ikaro-gray-medium)",
      color: "var(--ikaro-gray-dark)",
    },
  },
}));

const JobManager = ({
  jobType = "processamento",
  title = "Processamentos",
  subtitle = "Gerencie e monitore todos os processamentos",
  apiEndpoint = "/job/get_jobs_list",
  createJobPath = "/novo-processamento",
  dataPath = "jobs",
  columns = [],
  filterFields = [],
  customActions = [],
  customHeaderActions = [],
  onNewJob = null,
  onJobClick = null,
  onJobAction = null,
  customFilters = {},
  defaultFilters = {},
  showNewJobButton = false,
  cellId = null, // Mudança: removido valor padrão fixo
  code = null,
  /** Header ServiceName na API (ex.: DOCUMENT_CREATION); se omitido, usa `code`. */
  serviceName = null,
  newJobButtonLabel = null,
  hideDefaultActions = false,
  hideViewAction = false,
  refreshTrigger = null,
  enableSelection = false,
  onBatchArchive = null,
  onBatchUnarchive = null,
  initialJobId = null, // ID do job para abrir automaticamente
  /* Opcional: habilita o drawer lateral de detalhes na tabela. Quando
     definida, cada linha ganha um botão ">" à direita que abre o drawer
     com o conteúdo retornado por esta função. */
  renderJobDetails = null,
  drawerTitle = null,
}) => {
  const classes = useStyles();
  const { token, cell } = useAuth(); // Mudança: importando cell do contexto
  const { t } = useTranslation("jobs");
  const { t: tCommon } = useTranslation("common");
  const [selectedJobIds, setSelectedJobIds] = useState([]);

  // Determinar qual cell_id usar: prop fornecida ou célula selecionada no contexto
  const effectiveCellId = cellId || cell?.value;

  // Função para calcular os valores padrão das datas
  const getDefaultDateFilters = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      date_start: thirtyDaysAgo.toISOString().split("T")[0],
      date_end: today.toISOString().split("T")[0],
    };
  };

  // Combinar filtros padrão com valores padrão de data
  const combinedDefaultFilters = {
    ...getDefaultDateFilters(),
    ...defaultFilters,
  };

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(combinedDefaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    sortBy: "insertion_timestamp",
    sortOrder: "desc",
  });
  const [showCellConfirmation, setShowCellConfirmation] = useState(false);

  // URL base da API
  const API_BASE_URL = getApiBaseUrl();

  // Estabilizar customFilters com useMemo.
  // Em modo embed, mescla o listing_query da sessão para garantir que scope=me
  // (e outros filtros do ListMode) sejam aplicados mesmo em páginas que não
  // passam embedListingQuery explicitamente via customFilters.
  const stableCustomFilters = useMemo(
    () => {
      if (!isEmbedMode()) return customFilters;
      const embedQuery = getEmbedListingQuery();
      return { ...embedQuery, ...customFilters };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(customFilters)],
  );

  const fetchJobs = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page_size: pagination.pageSize,
        offset: (pagination.page - 1) * pagination.pageSize,
        sort_by: sortConfig.sortBy,
        sort_order: sortConfig.sortOrder,
        source_type: "user",
        ...stableCustomFilters,
      });

      // Adicionar filtros opcionais
      if (effectiveCellId) queryParams.append("cell_id", effectiveCellId);
      if (code) queryParams.append("code", code);
      if (
        filters.include_archived === true ||
        filters.include_archived === "true"
      ) {
        queryParams.append("include_archived", "true");
      }
      if (
        filters.include_inactive === true ||
        filters.include_inactive === "true"
      ) {
        queryParams.append("include_inactive", "true");
      }

      // Adicionar filtros personalizados
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          if (key === "company" && typeof value === "object" && value.id) {
            queryParams.append("company_id", value.id);
          } else if (
            key === "tenant" &&
            typeof value === "object" &&
            value.id
          ) {
            queryParams.append("tenant_id", value.id);
          } else if (typeof value === "string" && value.trim() !== "") {
            queryParams.append(key, value);
          }
        }
      });

      const url = `${API_BASE_URL}${apiEndpoint}?${queryParams}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
          ServiceName: serviceName || code,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          `Erro na requisição: ${response.status} ${
            data.detail || response.statusText
          }`,
        );
      }
      setJobs(data[dataPath] || []);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination?.total_count || 0,
        totalPages: data.pagination?.total_pages || 0,
      }));
    } catch (err) {
      console.error("Erro ao carregar jobs:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Carrega os jobs quando o componente é montado ou filtros/paginação mudam
  useEffect(() => {
    console.log("Fetching jobs with pagination:", pagination);
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.page,
    pagination.pageSize,
    sortConfig.sortBy,
    sortConfig.sortOrder,
    effectiveCellId,
    code,
    serviceName,
    filters,
    stableCustomFilters,
    refreshTrigger,
  ]);

  // Reseta a paginação para página 1 quando a célula muda
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [effectiveCellId]);

  // Auto-refresh: atualiza a lista de jobs a cada 10 segundos
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchJobs(true);
    }, 60000); // 60 segundos

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.page,
    pagination.pageSize,
    sortConfig.sortBy,
    sortConfig.sortOrder,
    effectiveCellId,
    code,
    serviceName,
    filters,
    stableCustomFilters,
  ]);

  // Abrir job automaticamente quando initialJobId é fornecido
  useEffect(() => {
    if (initialJobId && jobs.length > 0 && onJobClick) {
      const job = jobs.find((j) => j.id === initialJobId);
      if (job) {
        onJobClick(job);
      }
    }
  }, [initialJobId, jobs, onJobClick]);

  const handleRefresh = () => {
    fetchJobs(true);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters(combinedDefaultFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    console.log("Changing page to:", newPage);
    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1,
    }));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
  };

  const handleNewJob = (jobType) => {
    // Mostrar modal de confirmação antes de prosseguir
    if (jobType !== "usuarios") {
      setShowCellConfirmation(true);
    } else {
      handleConfirmNewJob();
    }
  };

  const handleConfirmNewJob = () => {
    if (onNewJob) {
      onNewJob();
    } else if (createJobPath) {
      window.location.href = createJobPath;
    }
  };

  const handleCancelNewJob = () => {
    setShowCellConfirmation(false);
  };

  const handleJobClick = (job) => {
    if (onJobClick) {
      onJobClick(job);
    }
  };

  const handleJobAction = (action, job) => {
    if (onJobAction) {
      onJobAction(action, job);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Criar as ações do header incluindo ações customizadas e refresh
  const headerActions = [
    ...customHeaderActions,
    {
      label: tCommon("refresh"),
      icon: <Refresh />,
      onClick: handleRefresh,
      variant: "outlined",
      className: classes.refreshButton,
      disabled: refreshing,
    },
  ];

  return (
    <Box className={classes.root}>
      {/* Header fixo no topo */}
      <Box className={classes.headerSection}>
        <JobHeader
          title={title}
          subtitle={subtitle}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          onNewJob={() => handleNewJob(jobType)}
          newJobButtonLabel={newJobButtonLabel}
          customActions={headerActions}
          showNewJobButton={showNewJobButton}
        />
      </Box>

      {/* Filtros (quando visíveis) */}
      {showFilters && (
        <Box className={classes.filtersSection}>
          <JobFilters
            filters={filters}
            filterFields={filterFields}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
        </Box>
      )}

      {/* Área principal da tabela - ocupa o espaço restante */}
      <Box className={classes.tableSection}>
        <Box className={classes.containerCompact}>
          <Paper className={classes.paper}>
            {loading ? (
              <Box className={classes.loading}>
                <CircularProgress />
                <Typography variant="body1" className="ml-16">
                  {t("table.loadingJobs")}
                </Typography>
              </Box>
            ) : (
              <>
                {enableSelection && selectedJobIds.length > 0 && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={3}
                    padding={2}
                    borderBottom="1px solid var(--ikaro-gray-medium)"
                    backgroundColor="var(--ikaro-gray-light)"
                  >
                    <Typography variant="body2">
                      {selectedJobIds.length} {t("table.selectedCount")}
                    </Typography>
                    {onBatchArchive && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          onBatchArchive(selectedJobIds);
                          setSelectedJobIds([]);
                        }}
                      >
                        {t("actions.archiveSelected")}
                      </Button>
                    )}
                    {onBatchUnarchive && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          onBatchUnarchive(selectedJobIds);
                          setSelectedJobIds([]);
                        }}
                      >
                        {t("actions.unarchiveSelected")}
                      </Button>
                    )}
                    <Button size="small" onClick={() => setSelectedJobIds([])}>
                      {tCommon("clear")}
                    </Button>
                  </Box>
                )}
                {/* Tabela com scroll */}
                <Box className={classes.tableWrapper}>
                  <JobTable
                    jobs={jobs}
                    columns={columns}
                    customActions={customActions}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onJobClick={handleJobClick}
                    onJobAction={handleJobAction}
                    hideDefaultActions={hideDefaultActions}
                    hideViewAction={hideViewAction}
                    selectionMode={enableSelection}
                    selectedIds={selectedJobIds}
                    onSelectionChange={setSelectedJobIds}
                    renderJobDetails={renderJobDetails}
                    drawerTitle={drawerTitle}
                  />
                </Box>

                {/* Paginação fixa na base */}
                <Box className={classes.paginationSection}>
                  <JobPagination
                    page={pagination.page}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Snackbar para erros */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseError} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Modal de confirmação de célula */}
      <CellConfirmationModal
        open={showCellConfirmation}
        onClose={handleCancelNewJob}
        onConfirm={handleConfirmNewJob}
        cellName={cell?.title}
        jobType={jobType}
      />
    </Box>
  );
};

export default JobManager;
